import React from "react";
import { Box } from "@mui/material";
import { getVlmState, VLM_COLORS } from "../utils/vlmStatus";

const CORNERS = [
  { id: "tl", cursor: "nw-resize", top: -5,  left: -5  },
  { id: "tr", cursor: "ne-resize", top: -5,  right: -5 },
  { id: "bl", cursor: "sw-resize", bottom: -5, left: -5 },
  { id: "br", cursor: "se-resize", bottom: -5, right: -5 },
];

// Junctions are pipe-network annotation, not physical equipment — same idea as
// Connection.js's arrowheads (see its comment): fixed screen-pixel size,
// unaffected by `scale`, so they stay legible without ballooning at high zoom
// or vanishing at low zoom. Matches the 16px diamond ConnectionCreationOverlay
// already uses for its "split pipe" hotspot marker.
const JUNCTION_SIZE = 16;

const Element = ({
  element,
  isSelected,
  isPreviewed,        // looked at from the side pane list, not opened
  isSource,
  isTarget,
  scale = 1,
  position,
  isDragging,
  // Handlers are shared by every element (stable references from DrawingView)
  // and receive the element, so an unchanged element never re-renders.
  onMouseDown,        // (e, element)
  onCornerMouseDown,  // (e, element, corner)
  onHoverChange,      // (elementId | null) — canvas pick mode only
  isPickable,
  isFixedEndpoint,
  isUnlinked,
}) => {
  const { boundingBox } = element;
  const isJunction = element.elementType === 'pipe_junction';
  const isTextLabel = element.elementType === 'text_label';
  const isRejected = element.metadata?.rejected === true;

  let borderColor = element.confirmed ? "success.main" : "primary.light";
  let backgroundColor = "rgba(255, 255, 255, 0.2)";
  let borderWidth = 2;
  let borderStyle = "solid";

  // Rejected is a soft-delete state (§0.2 item 6) — it wins over the
  // confirmed/unconfirmed coloring below, but a selected item still needs to
  // read as selected so it can be found and un-rejected.
  if (isRejected && !isSelected) {
    borderColor = "#f4716a";
    backgroundColor = "rgba(244, 113, 106, 0.08)";
    borderStyle = "dashed";
  }

  // Rapid-annotate: Claude working (pulsing violet, dashed) / answered, awaiting
  // confirm (orange) / failed (pink). getVlmState is null once confirmed or rejected.
  const vlmState = getVlmState(element);
  if (vlmState) {
    borderColor = VLM_COLORS[vlmState];
    backgroundColor = vlmState === 'review' ? "rgba(245, 165, 36, 0.10)" : "rgba(167, 139, 250, 0.08)";
    borderStyle = vlmState === 'pending' ? "dashed" : "solid";
  }

  // "Show unlinked" clean-up: not yet wired into the graph (graphCompleteness.js).
  // The user asked to see these, so it wins over the review colours above.
  if (isUnlinked && !isRejected) {
    borderColor = "#38bdf8";
    backgroundColor = "rgba(56, 189, 248, 0.10)";
    borderStyle = "dashed";
  }

  if (isPreviewed && !isSelected) {
    borderColor = "#2dd4bf";
    backgroundColor = "rgba(45, 212, 191, 0.12)";
    borderWidth = 3;
    borderStyle = "solid";
  }

  if (isSelected) {
    borderColor = "primary.main";
    backgroundColor = "rgba(13, 71, 161, 0.15)";
    borderWidth = 3;
  } else if (isSource) {
    borderColor = "success.main";
    backgroundColor = "rgba(76, 175, 80, 0.15)";
    borderWidth = 2;
  } else if (isTarget) {
    borderColor = "secondary.main";
    backgroundColor = "rgba(0, 131, 143, 0.15)";
    borderWidth = 2;
  }

  // Junctions render at a fixed size (see JUNCTION_SIZE above), so their
  // top-left corner has to be recomputed to keep the box centered on the
  // original CV-detected point at every zoom level, rather than inheriting
  // boundingBox's own top-left directly.
  const displayX = isJunction
    ? boundingBox.x * scale + (boundingBox.width * scale) / 2 - JUNCTION_SIZE / 2
    : position ? position.x : boundingBox.x * scale;
  const displayY = isJunction
    ? boundingBox.y * scale + (boundingBox.height * scale) / 2 - JUNCTION_SIZE / 2
    : position ? position.y : boundingBox.y * scale;

  // Text labels are annotation, not equipment — a filled box would sit on top
  // of (and visually compete with) the drawing's own printed text. Only show
  // an outline on hover/select/pick/reject; otherwise fully transparent,
  // letting the underlying drawing image read through unobstructed. Rejected
  // stays outlined regardless of selection so it's still findable in the
  // annotation pass-1 workflow (§0.2/§0.3) even for text.
  const textLabelIsOutlined = isSelected || isPreviewed || isPickable || isFixedEndpoint || isRejected || Boolean(vlmState) || isUnlinked;

  return (
    <Box
      data-hit-type="element"
      data-hit-id={element._id}
      onMouseDown={onMouseDown ? (e) => onMouseDown(e, element) : undefined}
      onMouseEnter={onHoverChange ? () => onHoverChange(String(element._id)) : undefined}
      onMouseLeave={onHoverChange ? () => onHoverChange(null) : undefined}
      // Geometry is per element; kept out of `sx` so elements that look alike
      // share one generated style class instead of each minting its own.
      style={{
        left: `${displayX}px`,
        top: `${displayY}px`,
        width: isJunction ? `${JUNCTION_SIZE}px` : `${boundingBox.width * scale}px`,
        height: isJunction ? `${JUNCTION_SIZE}px` : `${boundingBox.height * scale}px`,
      }}
      sx={{
        position: "absolute",
        border: isTextLabel
          ? (textLabelIsOutlined ? `1.5px dashed` : "1.5px solid transparent")
          : `${borderWidth}px ${borderStyle}`,
        // Diamond, not circle — pipes on a P&ID always meet at 90°, so a diamond reads as
        // a "meeting point" the same way the line-hotspot marker does (ConnectionCreationOverlay).
        transform: isJunction ? "rotate(45deg)" : undefined,
        borderColor: isPickable ? "#2dd4bf" : borderColor,
        backgroundColor: isTextLabel ? "transparent" : (isFixedEndpoint ? "rgba(120,150,200,0.08)" : backgroundColor),
        opacity: isFixedEndpoint ? 0.4 : isRejected && !isSelected && !isPreviewed ? 0.55 : 1,
        // Junctions are fixed points — not draggable — so never show the grab cursor.
        cursor: isPickable ? "pointer" : isJunction ? "pointer" : isDragging ? "grabbing" : "grab",
        zIndex: isSelected || isPreviewed ? 2 : 1,
        boxShadow: isSelected ? "0 0 8px rgba(0, 0, 0, 0.3)" : isPreviewed ? "0 0 0 4px rgba(45, 212, 191, 0.25)" : "none",
        "&:hover": isPickable ? {
          backgroundColor: "rgba(45,212,191,0.2)",
          borderColor: "#2dd4bf",
          boxShadow: "0 0 8px rgba(45,212,191,0.4)",
        } : isTextLabel ? {
          border: "1.5px dashed",
          borderColor: "primary.main",
        } : {
          backgroundColor: "rgba(13, 71, 161, 0.25)",
          borderColor: "primary.main",
          boxShadow: "0 0 5px rgba(0, 0, 0, 0.2)",
        },
        transition: isDragging ? "none" : "all 0.2s ease",
        animation: vlmState === 'pending' && !isSelected ? "vlmPulse 1.2s ease-in-out infinite" : undefined,
        "@keyframes vlmPulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.45 } },
        userSelect: "none",
        touchAction: "none",
        pointerEvents: isFixedEndpoint ? "none" : undefined,
      }}
    >
      {/* Junctions are fixed in place by the pipe intersection they were split from —
          no resize handles, so their geometry can't be pulled out of sync. */}
      {isSelected && !isDragging && !isJunction && onCornerMouseDown && CORNERS.map(({ id, cursor, ...pos }) => (
        <Box
          key={id}
          onMouseDown={(e) => {
            e.stopPropagation();
            onCornerMouseDown(e, element, id);
          }}
          sx={{
            position: "absolute",
            width: 10,
            height: 10,
            background: "#2dd4bf",
            border: "2px solid #fff",
            borderRadius: "2px",
            cursor,
            zIndex: 10,
            ...pos,
          }}
        />
      ))}
    </Box>
  );
};

export default React.memo(Element);